import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _classPrivateFieldInitSpec from "@babel/runtime/helpers/classPrivateFieldInitSpec";
import _classPrivateFieldSet from "@babel/runtime/helpers/classPrivateFieldSet2";
import _classPrivateFieldGet from "@babel/runtime/helpers/classPrivateFieldGet2";
import { i18n, initializeI18n } from "@next-core/i18n";
import { loadBricksImperatively } from "@next-core/loader";
import { deepFreeze, isObject } from "@next-core/utils/general";
import moment from "moment";
import "moment/locale/zh-cn.js";
import { createHistory } from "../history.js";
import { matchStoryboard } from "./matchStoryboard.js";
import { Router } from "./Router.js";
import { NS, locales } from "./i18n.js";
import { loadNotificationService } from "../Notification.js";
import { loadDialogService } from "../Dialog.js";
import { injectedBrickPackages } from "./injected.js";
import { hasInstalledApp } from "./hasInstalledApp.js";
import { listenDevtoolsEagerly } from "./devtools.js";
import { getV2RuntimeFromDll } from "../getV2RuntimeFromDll.js";
import { customizeColorTheme } from "./customizeColorTheme.js";
let runtime;

// Allow inject bootstrap data in a runtime other than Brick Next.
let bootstrapData;
let router;
let processedBrickPackages;
export let hooks;
export function createRuntime(options) {
  if (runtime) {
    throw new Error("Cannot create multiple runtimes");
  }
  listenDevtoolsEagerly();
  hooks = options === null || options === void 0 ? void 0 : options.hooks;
  initializeI18n(NS, locales);
  moment.locale(i18n.language);
  i18n.on("languageChanged", () => {
    moment.locale(i18n.language);
  });
  createHistory();
  runtime = new Runtime();
  return runtime;
}
function getRuntimeV3() {
  return runtime;
}

// istanbul ignore next
function getRuntimeV2Factory() {
  const v2Kit = getV2RuntimeFromDll();
  if (v2Kit) {
    return function getRuntimeV2() {
      return new Proxy(v2Kit.getRuntime(), {
        get() {
          const key = arguments.length <= 1 ? undefined : arguments[1];
          switch (key) {
            case "getCurrentApp":
            case "getRecentApps":
            case "hasInstalledApp":
            case "getDesktops":
            case "getLaunchpadSettings":
            case "getLaunchpadSiteMap":
            case "toggleLaunchpadEffect":
            case "applyPageTitle":
            case "getNavConfig":
            case "getFeatureFlags":
            case "getMiscSettings":
            case "getBrandSettings":
              return Reflect.get(...arguments);
          }
        }
      });
    };
  }
}

// istanbul ignore next
export const getRuntime = getRuntimeV2Factory() || getRuntimeV3;
var _initialized = /*#__PURE__*/new WeakMap();
var _bootstrapped = /*#__PURE__*/new WeakMap();
export class Runtime {
  constructor() {
    _classPrivateFieldInitSpec(this, _initialized, false);
    _classPrivateFieldInitSpec(this, _bootstrapped, false);
    _defineProperty(this, "version", 3);
  }
  initialize(data) {
    var _data$settings;
    if (_classPrivateFieldGet(_initialized, this)) {
      throw new Error("The runtime cannot be initialized more than once");
    }
    _classPrivateFieldSet(_initialized, this, true);
    normalizeBootstrapData(data);
    bootstrapData = data;
    const {
      notification,
      dialog
    } = _internalApiGetPresetBricks();
    if (notification !== false) {
      loadNotificationService(notification !== null && notification !== void 0 ? notification : "basic.show-notification", this.loadBricks);
    }
    if (dialog !== false) {
      loadDialogService(dialog !== null && dialog !== void 0 ? dialog : "basic.show-dialog", this.loadBricks);
    }
    customizeColorTheme((_data$settings = data.settings) === null || _data$settings === void 0 || (_data$settings = _data$settings.misc) === null || _data$settings === void 0 ? void 0 : _data$settings.theme);
  }
  async bootstrap(data) {
    if (data) {
      this.initialize(data);
    }
    if (_classPrivateFieldGet(_bootstrapped, this)) {
      throw new Error("The runtime cannot be bootstrapped more than once");
    }
    _classPrivateFieldSet(_bootstrapped, this, true);
    router = new Router(bootstrapData.storyboards);
    await router.bootstrap();
  }
  getRecentApps() {
    var _router$getRecentApps, _router;
    return (_router$getRecentApps = (_router = router) === null || _router === void 0 ? void 0 : _router.getRecentApps()) !== null && _router$getRecentApps !== void 0 ? _router$getRecentApps : {};
  }
  getCurrentApp() {
    var _router2;
    return (_router2 = router) === null || _router2 === void 0 ? void 0 : _router2.getRecentApps().currentApp;
  }
  hasInstalledApp(appId, matchVersion) {
    return hasInstalledApp(appId, matchVersion);
  }
  getFeatureFlags() {
    var _bootstrapData, _router3;
    return {
      ...((_bootstrapData = bootstrapData) === null || _bootstrapData === void 0 || (_bootstrapData = _bootstrapData.settings) === null || _bootstrapData === void 0 ? void 0 : _bootstrapData.featureFlags),
      ...((_router3 = router) === null || _router3 === void 0 || (_router3 = _router3.getRecentApps().currentApp) === null || _router3 === void 0 || (_router3 = _router3.config) === null || _router3 === void 0 || (_router3 = _router3.settings) === null || _router3 === void 0 ? void 0 : _router3.featureFlags),
      "migrate-to-brick-next-v3": true
    };
  }
  getMiscSettings() {
    var _bootstrapData2, _router4;
    return {
      ...((_bootstrapData2 = bootstrapData) === null || _bootstrapData2 === void 0 || (_bootstrapData2 = _bootstrapData2.settings) === null || _bootstrapData2 === void 0 ? void 0 : _bootstrapData2.misc),
      ...((_router4 = router) === null || _router4 === void 0 || (_router4 = _router4.getRecentApps().currentApp) === null || _router4 === void 0 || (_router4 = _router4.config) === null || _router4 === void 0 || (_router4 = _router4.settings) === null || _router4 === void 0 ? void 0 : _router4.misc)
    };
  }
  getBrandSettings() {
    var _bootstrapData3;
    return {
      base_title: "DevOps 管理专家",
      ...((_bootstrapData3 = bootstrapData) === null || _bootstrapData3 === void 0 || (_bootstrapData3 = _bootstrapData3.settings) === null || _bootstrapData3 === void 0 ? void 0 : _bootstrapData3.brand)
    };
  }
  getLaunchpadSettings() {
    var _bootstrapData4;
    return {
      columns: 7,
      rows: 4,
      ...((_bootstrapData4 = bootstrapData) === null || _bootstrapData4 === void 0 || (_bootstrapData4 = _bootstrapData4.settings) === null || _bootstrapData4 === void 0 ? void 0 : _bootstrapData4.launchpad)
    };
  }
  getDesktops() {
    var _bootstrapData$deskto, _bootstrapData5;
    return (_bootstrapData$deskto = (_bootstrapData5 = bootstrapData) === null || _bootstrapData5 === void 0 ? void 0 : _bootstrapData5.desktops) !== null && _bootstrapData$deskto !== void 0 ? _bootstrapData$deskto : [];
  }
  getLaunchpadSiteMap() {
    var _bootstrapData$siteSo, _bootstrapData6;
    return (_bootstrapData$siteSo = (_bootstrapData6 = bootstrapData) === null || _bootstrapData6 === void 0 ? void 0 : _bootstrapData6.siteSort) !== null && _bootstrapData$siteSo !== void 0 ? _bootstrapData$siteSo : [];
  }
  toggleLaunchpadEffect(open) {
    document.body.classList.toggle("launchpad-open", open);
  }
  applyPageTitle(pageTitle) {
    var _router5;
    const baseTitle = this.getBrandSettings().base_title;
    document.title = ((_router5 = router) === null || _router5 === void 0 || (_router5 = _router5.getRecentApps().currentApp) === null || _router5 === void 0 ? void 0 : _router5.localeTitle) || (pageTitle ? `${pageTitle} - ${baseTitle}` : baseTitle);
  }
  getNavConfig() {
    var _router6;
    return (_router6 = router) === null || _router6 === void 0 ? void 0 : _router6.getNavConfig();
  }
  loadBricks(bricks) {
    return loadBricksImperatively(bricks, getBrickPackages());
  }
}
function normalizeBootstrapData(data) {
  if (isObject(data.settings)) {
    deepFreeze(data.settings);
  }
  if (data.brickPackages) {
    deepFreeze(data.brickPackages);
  }
}
function processPublicDepsPackages(brickPackages, pubDeps) {
  if (!(pubDeps !== null && pubDeps !== void 0 && pubDeps.length)) return brickPackages;
  const bricksMap = new Map();

  // bootstrapData 数据和 pubDeps 中可能同时存在同一个包名，需要过滤去重， 以 pubDeps 中的包为准
  [...pubDeps, ...brickPackages].forEach(pkg => {
    const pkgName = pkg.filePath.split("/")[1];
    // 始终将 pubDeps 放在前面
    if (!bricksMap.has(pkgName)) {
      bricksMap.set(pkgName, pkg);
    }
  });
  return Array.from(bricksMap.values());
}
export function getBrickPackages() {
  var _ref, _ref2, _bootstrapData$brickP, _bootstrapData7;
  return (// Not necessary to process brick packages multiple times.
    processedBrickPackages !== null && processedBrickPackages !== void 0 ? processedBrickPackages : processedBrickPackages = processPublicDepsPackages((_ref = (_ref2 = (_bootstrapData$brickP = (_bootstrapData7 = bootstrapData) === null || _bootstrapData7 === void 0 ? void 0 : _bootstrapData7.brickPackages) !== null && _bootstrapData$brickP !== void 0 ? _bootstrapData$brickP : injectedBrickPackages) !== null && _ref2 !== void 0 ? _ref2 : window.STANDALONE_BRICK_PACKAGES) !== null && _ref !== void 0 ? _ref : [], window.PUBLIC_DEPS)
  );
}
export function _internalApiGetPresetBricks() {
  var _bootstrapData$settin, _bootstrapData8;
  return (_bootstrapData$settin = (_bootstrapData8 = bootstrapData) === null || _bootstrapData8 === void 0 || (_bootstrapData8 = _bootstrapData8.settings) === null || _bootstrapData8 === void 0 ? void 0 : _bootstrapData8.presetBricks) !== null && _bootstrapData$settin !== void 0 ? _bootstrapData$settin : {};
}
export function _internalApiGetRenderId() {
  var _router7;
  return (_router7 = router) === null || _router7 === void 0 ? void 0 : _router7.getRenderId();
}
export function _internalApiMatchStoryboard(pathname) {
  var _bootstrapData$storyb, _bootstrapData9;
  return matchStoryboard((_bootstrapData$storyb = (_bootstrapData9 = bootstrapData) === null || _bootstrapData9 === void 0 ? void 0 : _bootstrapData9.storyboards) !== null && _bootstrapData$storyb !== void 0 ? _bootstrapData$storyb : [], pathname);
}
export function _internalApiGetRuntimeContext() {
  var _router8;
  return (_router8 = router) === null || _router8 === void 0 ? void 0 : _router8.getRuntimeContext();
}
export function _internalApiGetStoryboardInBootstrapData(appId) {
  var _bootstrapData0;
  return (_bootstrapData0 = bootstrapData) === null || _bootstrapData0 === void 0 || (_bootstrapData0 = _bootstrapData0.storyboards) === null || _bootstrapData0 === void 0 ? void 0 : _bootstrapData0.find(storyboard => storyboard.app.id === appId);
}
export function _internalApiGetAppInBootstrapData(appId) {
  var _internalApiGetStoryb;
  return (_internalApiGetStoryb = _internalApiGetStoryboardInBootstrapData(appId)) === null || _internalApiGetStoryb === void 0 ? void 0 : _internalApiGetStoryb.app;
}
export let _test_only_setBootstrapData;

// istanbul ignore next
if (process.env.NODE_ENV === "test") {
  _test_only_setBootstrapData = data => {
    bootstrapData = data;
  };
}
//# sourceMappingURL=Runtime.js.map